/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { SwitchTransition, CSSTransition } from 'react-transition-group'
import { useRef, useState, useEffect } from 'react'

const DURATION = 300 // ms – smooth transition duration

/**
 * Smoothly swaps header elements using SwitchTransition from react-transition-group.
 * Provides a natural transition between different header variants to eliminate jank.
 *
 * @param {string} headerKey - Unique key that identifies the current header variant
 * @param {React.ReactNode} children - The header component to render
 */
export default function HeaderSwitchTransition({ headerKey, children }) {
  const nodeRef = useRef(null)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (e) => setPrefersReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // If reduced motion is preferred, skip animations
  if (prefersReducedMotion) {
    return <div>{children}</div>
  }

  return (
    <SwitchTransition>
      <CSSTransition
        key={headerKey}
        nodeRef={nodeRef}
        timeout={DURATION}
        classNames='header-switch'
        unmountOnExit
      >
        <div
          ref={nodeRef}
          css={css`
            /* Base styles */
            &.header-switch-enter {
              opacity: 0;
              transform: translateY(8px) scale(0.98);
            }

            &.header-switch-enter-active {
              opacity: 1;
              transform: translateY(0) scale(1);
              transition: all ${DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
            }

            &.header-switch-exit {
              opacity: 1;
              transform: translateY(0) scale(1);
            }

            &.header-switch-exit-active {
              opacity: 0;
              transform: translateY(-8px) scale(0.98);
              transition: all ${DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
            }
          `}
        >
          {children}
        </div>
      </CSSTransition>
    </SwitchTransition>
  )
}
